import { useEffect, useState } from "react";
import { customerManager } from "../../data/managers/CustomerManager";
import { Customer } from "../../data/models/customers/CustomerModel";
import { useLoading } from "../../data/providers/LoadingProvider";
import { useSnackbar } from "../../data/providers/SnackbarProvider";

export const useCustomers = () => {
  const { showSnackbar, showError } = useSnackbar();
  const { startLoading, stopLoading, loading } = useLoading();

  useEffect(() => {
    refreshCustomers();
  }, []);

  const [customers, setCustomers] = useState<Customer[]>([]);

  const refreshCustomers = async () => {
    try {
      startLoading();
      const resp = await customerManager.getAllCustomers();
      if (!resp) {
        showError("No se pudieron cargar los clientes");
        return;
      }
      setCustomers(resp.map((p: any) => new Customer(p)));
    } catch (err) {
      showError("Error al obtener clientes");
    } finally {
      stopLoading();
    }
  };

  const createCustomer = async (data: Omit<Customer, "id">) => {
    try {
      startLoading();
      const customer: Customer = {
        ...data,
      };
      const r = await customerManager.updateOrCreateCustomer(customer);
      if (!r) throw new Error("On create customer");
      showSnackbar("Cliente creado exitosamente");
    } catch (err) {
      showError("No se pudo crear el cliente");
    } finally {
      await refreshCustomers();
      stopLoading();
    }
  };

  const updateCustomer = async (
    id: string,
    data: Partial<Omit<Customer, "id">>
  ) => {
    try {
      startLoading();
      const existing = customers.find((e) => e.id === id);
      if (!existing) throw new Error("Customer not found");
      const updatedCustomer = {
        ...existing,
        ...data,
      };
      const r = await customerManager.updateOrCreateCustomer(updatedCustomer);
      if (!r) throw new Error("Update error");
      showSnackbar("Cliente actualizado correctamente");
    } catch (err) {
      showError("No se pudo actualizar el cliente");
    } finally {
      await refreshCustomers();
      stopLoading();
    }
  };

  const deleteCustomer = async (id: string) => {
    try {
      startLoading();
      const customer = customers.find((e) => e.id === id);
      if (!customer) throw new Error("Customer not found");
      const r = await customerManager.deleteCustomer(customer!);
      if (!r) throw new Error("On delete customer");
      showSnackbar("Cliente eliminado");
    } catch (err) {
      showError("No se pudo eliminar el cliente");
    } finally {
      await refreshCustomers();
      stopLoading();
    }
  };

  return {
    customers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    refreshCustomers,
    loading,
  };
};
